import { useState, useEffect, useCallback, useRef } from 'react';
import { WebSocketMessage, WebSocketConnection, WebSocketConfig, RealTimeEvent } from '../types';
import { webSocketManager } from '../services/websocket';

export interface UseWebSocketOptions {
  channel: string;
  config?: Partial<WebSocketConfig>;
  autoConnect?: boolean;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: any) => void;
  onMessage?: (message: WebSocketMessage) => void;
}

export interface UseWebSocketReturn {
  // Connection state
  isConnected: boolean;
  connectionStatus: WebSocketConnection;
  lastMessage: WebSocketMessage | null;
  
  // Actions
  connect: () => Promise<void>;
  disconnect: () => void;
  send: (message: Omit<WebSocketMessage, 'timestamp'>) => void;
  subscribe: (type: string, callback: (message: WebSocketMessage) => void) => () => void;
  
  // Event handling
  events: RealTimeEvent[];
  clearEvents: () => void;
}

export function useWebSocket({
  channel,
  config,
  autoConnect = false,
  onConnect,
  onDisconnect,
  onError,
  onMessage,
}: UseWebSocketOptions): UseWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<WebSocketConnection>({
    isConnected: false,
    reconnectAttempts: 0,
    maxReconnectAttempts: 5,
    reconnectDelay: 1000,
  });
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [events, setEvents] = useState<RealTimeEvent[]>([]);
  
  const serviceRef = useRef(webSocketManager.getService(channel, config));
  const subscriptionsRef = useRef<Set<() => void>>(new Set());

  // Update connection status
  const updateConnectionStatus = useCallback(() => {
    const status = serviceRef.current.getConnectionStatus();
    setConnectionStatus(status);
    setIsConnected(status.isConnected);
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    try {
      await serviceRef.current.connect();
      updateConnectionStatus();
      onConnect?.();
    } catch (error) {
      onError?.(error);
      throw error;
    }
  }, [updateConnectionStatus, onConnect, onError]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    serviceRef.current.disconnect();
    updateConnectionStatus();
    onDisconnect?.();
  }, [updateConnectionStatus, onDisconnect]);

  // Send message
  const send = useCallback((message: Omit<WebSocketMessage, 'timestamp'>) => {
    try {
      serviceRef.current.send(message);
    } catch (error) {
      onError?.(error);
      throw error;
    }
  }, [onError]);

  // Subscribe to message types
  const subscribe = useCallback((type: string, callback: (message: WebSocketMessage) => void) => {
    const unsubscribe = serviceRef.current.subscribe(type, callback);
    subscriptionsRef.current.add(unsubscribe);
    
    // Return cleanup function
    return () => {
      unsubscribe();
      subscriptionsRef.current.delete(unsubscribe);
    };
  }, []);

  // Clear events
  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  // Set up subscriptions and auto-connect
  useEffect(() => {
    // Subscribe to connection events
    const unsubscribeConnection = serviceRef.current.subscribe('connection', (message) => {
      updateConnectionStatus();
      
      if (message.data?.status === 'connected') {
        onConnect?.();
      } else if (message.data?.status === 'disconnected') {
        onDisconnect?.();
      }
    });

    // Subscribe to error events
    const unsubscribeError = serviceRef.current.subscribe('error', (message) => {
      onError?.(message.data);
    });

    // Subscribe to all messages
    const unsubscribeMessages = serviceRef.current.subscribe('message', (message) => {
      setLastMessage(message);
      onMessage?.(message);
      
      // Convert certain message types to events
      if (['alert', 'connection', 'error'].includes(message.type)) {
        const event: RealTimeEvent = {
          id: message.id || `event_${Date.now()}`,
          type: message.type === 'alert' ? 'alert_triggered' : 
                message.type === 'connection' ? 'connection_status' : 'system_event',
          severity: message.type === 'error' ? 'error' : 
                   message.type === 'alert' ? 'warning' : 'info',
          message: message.data?.message || `${message.type} event`,
          data: message.data,
          timestamp: message.timestamp,
          source: channel,
        };
        
        setEvents(prev => [event, ...prev.slice(0, 49)]); // Keep last 50 events
      }
    });

    // Store cleanup functions
    subscriptionsRef.current.add(unsubscribeConnection);
    subscriptionsRef.current.add(unsubscribeError);
    subscriptionsRef.current.add(unsubscribeMessages);

    // Auto-connect if requested
    if (autoConnect) {
      connect().catch(error => {
        console.error('Auto-connect failed:', error);
      });
    }

    // Cleanup on unmount
    return () => {
      subscriptionsRef.current.forEach(unsubscribe => unsubscribe());
      subscriptionsRef.current.clear();
    };
  }, [channel, autoConnect, connect, updateConnectionStatus, onConnect, onDisconnect, onError, onMessage]);

  return {
    isConnected,
    connectionStatus,
    lastMessage,
    connect,
    disconnect,
    send,
    subscribe,
    events,
    clearEvents,
  };
}

/**
 * Hook for real-time analytics updates
 */
export function useAnalyticsWebSocket() {
  return useWebSocket({
    channel: 'analytics',
    autoConnect: true,
    config: {
      heartbeatInterval: 30000, // 30 seconds
    },
  });
}

/**
 * Hook for system health monitoring
 */
export function useHealthWebSocket() {
  return useWebSocket({
    channel: 'health',
    autoConnect: true,
    config: {
      heartbeatInterval: 15000, // 15 seconds
    },
  });
}

/**
 * Hook for OAuth flow monitoring
 */
export function useOAuthWebSocket(flowId?: string) {
  return useWebSocket({
    channel: flowId ? `oauth/${flowId}` : 'oauth',
    autoConnect: !!flowId,
    config: {
      heartbeatInterval: 10000, // 10 seconds for faster OAuth updates
    },
  });
}

/**
 * Hook for configuration updates
 */
export function useConfigWebSocket(userId?: string) {
  return useWebSocket({
    channel: userId ? `config/${userId}` : 'config',
    autoConnect: !!userId,
    config: {
      heartbeatInterval: 60000, // 1 minute
    },
  });
}